import { getSpecialtiesByOrganization } from '@/lib/api/specialties/specialties-api'
import { Specialty } from '@/lib/api/common/interfaces/specialty.interface'
import {
    getOnboardingStatus,
    saveOnboardingDraft,
    submitOnboarding,
} from '@/lib/api/onboarding/onboarding-api'
import { unMask } from '@/lib/form-state/util'
import { useAuth } from '@/store/hooks'
import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { MdArrowBack, MdArrowForward, MdCheckCircle } from 'react-icons/md'
import { toast } from 'react-toastify'
import {
    costPerConsultation,
    emptyFormState,
    looksSurgical,
    OnboardingFormState,
    OnboardingStep,
    STEP_TITLES,
    stepsFor,
} from './onboarding-state'
import CostsStep from './steps/costs-step'
import DigitalAccountStep from './steps/digital-account-step'
import DoctorStep from './steps/doctor-step'
import PricingStep from './steps/pricing-step'
import ProfileStep from './steps/profile-step'
import ReviewStep from './steps/review-step'
import SpecialtiesStep from './steps/specialties-step'
import SurgeriesStep from './steps/surgeries-step'

interface Props {
    /** Fecha o modal — usado pelo "Continuar depois" e ao concluir. */
    onClose: () => void
    /** Assistente concluído: o guard não deve reabrir. */
    onCompleted: () => void
}

/**
 * Assistente de primeira entrada do segmento de saúde.
 *
 * Abre depois que o cadastro obrigatório está completo (ver `RegistrationGuard`) e
 * só uma vez por conta. **Não é bloqueante:** o "Continuar depois" salva o estado
 * e devolve o painel — travar o médico num modal por causa do preço da consulta
 * seria pior do que uma conta sem catálogo.
 */
export default function OnboardingModal({ onClose, onCompleted }: Props) {
    const { user } = useAuth()
    const navigate = useNavigate()
    const [form, setForm] = useState<OnboardingFormState>(emptyFormState)
    const [step, setStep] = useState<OnboardingStep>('profile')
    const [specialtyOptions, setSpecialtyOptions] = useState<Specialty[]>([])
    const [saving, setSaving] = useState(false)
    const [submitting, setSubmitting] = useState(false)
    // Assim que o médico responde "sou cirurgião", a resposta é dele: a marcação
    // automática abaixo para de mexer na caixa.
    const [surgeonTouched, setSurgeonTouched] = useState(false)
    // O que está digitado no campo de especialidade e ainda não virou item.
    const [specialtyDraft, setSpecialtyDraft] = useState('')

    const steps = useMemo(() => stepsFor(form.profile), [form.profile])
    const stepIndex = Math.max(0, steps.indexOf(step))
    const isLastStep = step === 'review'
    /** Assistente já gravado: a tela final não é mais um passo a preencher. */
    const isDone = step === 'digitalAccount'

    useEffect(() => {
        const load = async () => {
            const res = await getOnboardingStatus()
            if (res.success && res.result?.answers) {
                const draft = res.result.answers as Partial<OnboardingFormState> & {
                    step?: OnboardingStep
                }
                setForm(current => ({
                    ...current,
                    ...draft,
                    doctor: { ...current.doctor, ...(draft.doctor ?? {}) },
                }))
                if (draft.step && STEP_TITLES[draft.step]) setStep(draft.step)
            } else {
                // Sem rascunho: o nome do médico começa com o do usuário logado.
                setForm(current => ({
                    ...current,
                    doctor: { ...current.doctor, name: user?.name ?? '' },
                }))
            }
        }
        load()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
        const loadSpecialties = async () => {
            if (!user?.organizationId) return
            const res = await getSpecialtiesByOrganization(user.organizationId)
            if (res.success && Array.isArray(res.result)) setSpecialtyOptions(res.result)
        }
        loadSpecialties()
    }, [user?.organizationId])

    // A caixa "Eu realizo cirurgias" nasce DESMARCADA — a maioria dos médicos não
    // opera, e marcar por conta própria cadastraria cirurgia em conta de quem só
    // consulta. Só marca sozinha quando a especialidade traz a palavra ("Cirurgia
    // Geral", "Neurocirurgia"): aí a resposta já está no que o médico escreveu.
    useEffect(() => {
        if (surgeonTouched) return
        if (!looksSurgical(form.specialties)) return
        setForm(current => ({ ...current, isSurgeon: true }))
    }, [form.specialties, surgeonTouched])

    // O campo de especialidade desmonta junto com a etapa e nasce vazio ao voltar.
    // Sem zerar aqui, o texto de uma visita anterior continuaria travando o
    // "Continuar" por algo que já não está na tela.
    useEffect(() => {
        if (step !== 'specialties') setSpecialtyDraft('')
    }, [step])

    /** Campos vazios de uma lista, no formato do aviso ao usuário. */
    const missingLabels = (
        fields: Array<{ value?: string | null; label: string }>
    ): string[] => fields.filter(field => !field.value?.trim()).map(field => field.label)

    const validateStep = (): string | null => {
        if (step === 'profile' && !form.profile) {
            return 'Escolha como você atende para continuar.'
        }
        if (step === 'doctor') {
            const { doctor } = form
            const missing = missingLabels([
                { value: doctor.name, label: 'Nome completo' },
                { value: doctor.phoneNumber, label: 'Telefone' },
                { value: doctor.document, label: 'CPF' },
                { value: doctor.councilName, label: 'Conselho' },
                { value: doctor.registrationNumber, label: 'Número do registro' },
                { value: doctor.address.zipCode, label: 'CEP' },
                { value: doctor.address.street, label: 'Rua' },
                { value: doctor.address.number, label: 'Número' },
                { value: doctor.address.neighborhood, label: 'Bairro' },
                { value: doctor.address.city, label: 'Cidade' },
                { value: doctor.address.state, label: 'UF' },
            ])
            if (missing.length > 0) {
                return `Preencha os campos obrigatórios: ${missing.join(', ')}.`
            }
            // Só a contagem de dígitos: o sistema inteiro trata CPF e telefone
            // assim, e inventar validação de dígito verificador só aqui recusaria
            // cadastro que as outras telas aceitam.
            if (unMask(doctor.document).length !== 11) {
                return 'O CPF precisa ter 11 dígitos.'
            }
            if (unMask(doctor.phoneNumber).length < 10) {
                return 'Informe o telefone com DDD.'
            }
        }
        if (step === 'costs') {
            const filled = form.costs.filter(
                cost => cost.description.trim() && cost.amount > 0
            )
            if (filled.length === 0) {
                return 'Adicione ao menos um custo, com descrição e valor.'
            }
            if (filled.length < form.costs.length) {
                return 'Preencha a descrição e o valor de todos os custos, ou remova os que sobraram.'
            }
            if (form.monthlyConsultations <= 0) {
                return 'Informe quantas consultas você faz por mês, em média.'
            }
        }
        if (step === 'specialties') {
            // Texto digitado que não virou item: avançar aqui apagaria a
            // especialidade em silêncio, junto com a etapa.
            if (specialtyDraft.trim()) {
                return `Clique em "Adicionar" para incluir "${specialtyDraft.trim()}" na lista.`
            }
            if (form.specialties.length === 0) {
                return 'Adicione ao menos uma especialidade para continuar.'
            }
        }
        return null
    }

    const goNext = () => {
        const error = validateStep()
        if (error) {
            toast.warn(error)
            return
        }
        const next = steps[stepIndex + 1]
        if (next) setStep(next)
    }

    const goBack = () => {
        const previous = steps[stepIndex - 1]
        if (previous) setStep(previous)
    }

    const handleSaveForLater = async () => {
        setSaving(true)
        try {
            const res = await saveOnboardingDraft({
                profile: form.profile ?? undefined,
                answers: { ...form, step },
            })
            if (res.success) {
                toast.success('Salvamos o que você preencheu. É só continuar depois.')
                onClose()
            } else {
                toast.error(res.error || 'Não foi possível salvar o que você preencheu.')
            }
        } finally {
            setSaving(false)
        }
    }

    const handleSubmit = async () => {
        if (!form.profile) {
            toast.warn('Escolha como você atende para continuar.')
            return
        }

        setSubmitting(true)
        try {
            const res = await submitOnboarding({
                profile: form.profile,
                ...(form.profile === 'INDIVIDUAL_DOCTOR'
                    ? {
                          doctor: {
                              name: form.doctor.name.trim(),
                              registrationNumber: form.doctor.registrationNumber || undefined,
                              councilName: form.doctor.councilName || undefined,
                              // Telefone e CPF vão sem máscara, como no cadastro de
                              // profissional: quem compara telefone compara dígitos, e
                              // o mesmo CPF gravado ora com ora sem pontuação vira dois
                              // cadastros diferentes na busca.
                              document: unMask(form.doctor.document) || undefined,
                              phoneNumber: unMask(form.doctor.phoneNumber) || undefined,
                              address: form.doctor.address,
                          },
                          specialties: form.specialties,
                          costs: form.costs.filter(cost => cost.description.trim()),
                          monthlyConsultations: form.monthlyConsultations || undefined,
                          isSurgeon: form.isSurgeon,
                          surgeries: form.isSurgeon ? form.surgeries : [],
                      }
                    : {}),
            })

            if (res.success) {
                toast.success('Tudo pronto! Sua conta já está configurada.')
                // Não fecha ainda: a conta digital é o passo que falta, e é agora que
                // o dono da conta está com a atenção no assunto.
                setStep('digitalAccount')
            } else {
                toast.error(res.error || 'Não foi possível concluir a configuração.')
            }
        } finally {
            setSubmitting(false)
        }
    }

    const renderStep = () => {
        switch (step) {
            case 'profile':
                return (
                    <ProfileStep
                        value={form.profile}
                        onChange={profile => setForm({ ...form, profile })}
                    />
                )
            case 'doctor':
                return (
                    <DoctorStep
                        value={form.doctor}
                        onChange={doctor => setForm({ ...form, doctor })}
                    />
                )
            case 'specialties':
                return (
                    <SpecialtiesStep
                        value={form.specialties}
                        suggestions={specialtyOptions}
                        onChange={specialties => setForm({ ...form, specialties })}
                        onDraftChange={setSpecialtyDraft}
                    />
                )
            case 'costs':
                return (
                    <CostsStep
                        value={form.costs}
                        monthlyConsultations={form.monthlyConsultations}
                        onChange={costs => setForm({ ...form, costs })}
                        onChangeMonthlyConsultations={monthlyConsultations =>
                            setForm({ ...form, monthlyConsultations })
                        }
                    />
                )
            case 'pricing':
                return (
                    <PricingStep
                        value={form.specialties}
                        cost={costPerConsultation(form.costs, form.monthlyConsultations)}
                        onChange={specialties => setForm({ ...form, specialties })}
                    />
                )
            case 'surgeries':
                return (
                    <SurgeriesStep
                        isSurgeon={form.isSurgeon}
                        value={form.surgeries}
                        hinted={looksSurgical(form.specialties)}
                        onChangeIsSurgeon={isSurgeon => {
                            setSurgeonTouched(true)
                            setForm({ ...form, isSurgeon })
                        }}
                        onChange={surgeries => setForm({ ...form, surgeries })}
                    />
                )
            case 'review':
                return <ReviewStep form={form} />
            case 'digitalAccount':
                return <DigitalAccountStep />
        }
    }

    return (
        <div className="fixed inset-0 z-[9998] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
            <div className="bg-base-100 rounded-lg shadow-2xl w-full !max-w-3xl max-h-[92vh] flex flex-col">
                <header className="border-b border-base-300 p-6 pb-4">
                    <div className="flex items-center gap-3 mb-2">
                        <MdCheckCircle className="text-2xl text-secondary" />
                        <h2 className="text-xl md:text-2xl font-bold text-base-content">
                            {STEP_TITLES[step]}
                        </h2>
                    </div>

                    <div className="flex items-center gap-2">
                        {steps.map((item, index) => (
                            <span
                                key={item}
                                className={`h-1.5 flex-1 rounded-full ${
                                    isDone || index <= stepIndex
                                        ? 'bg-secondary'
                                        : 'bg-base-300'
                                }`}
                            />
                        ))}
                    </div>
                    <p className="mt-2 text-sm text-base-content/60">
                        {isDone
                            ? 'Configuração concluída'
                            : `Etapa ${stepIndex + 1} de ${steps.length}`}
                    </p>
                </header>

                {/* `overflow-x-hidden` é rede de segurança: um texto auxiliar longo
                    dentro de `label` (que não quebra linha) já esticou o modal e criou
                    barra horizontal. A correção real é o texto quebrar; isto impede que
                    a próxima reincidência empurre a tela inteira. */}
                <div className="flex-1 min-w-0 overflow-y-auto overflow-x-hidden p-6">
                    {renderStep()}
                </div>

                <footer className="border-t border-base-300 p-6 pt-4 flex flex-wrap items-center justify-between gap-3">
                    {isDone ? (
                        // Já está tudo gravado: aqui "depois" fecha o assistente, não
                        // guarda rascunho nenhum.
                        <button
                            type="button"
                            className="btn btn-ghost btn-sm"
                            onClick={onCompleted}
                        >
                            Ativar depois
                        </button>
                    ) : (
                        <button
                            type="button"
                            className="btn btn-ghost btn-sm"
                            onClick={handleSaveForLater}
                            disabled={saving || submitting}
                        >
                            {saving ? 'Salvando...' : 'Continuar depois'}
                        </button>
                    )}

                    <div className="flex items-center gap-2">
                        {!isDone && stepIndex > 0 && (
                            <button
                                type="button"
                                className="btn btn-outline gap-1"
                                onClick={goBack}
                                disabled={submitting}
                            >
                                <MdArrowBack className="text-lg" />
                                Voltar
                            </button>
                        )}

                        {isDone ? (
                            <button
                                type="button"
                                className="btn btn-secondary gap-1"
                                onClick={() => {
                                    onCompleted()
                                    navigate('/financial/digital-account')
                                }}
                            >
                                Ativar conta digital
                                <MdArrowForward className="text-lg" />
                            </button>
                        ) : isLastStep ? (
                            <button
                                type="button"
                                className="btn btn-secondary gap-1"
                                onClick={handleSubmit}
                                disabled={submitting}
                            >
                                {submitting ? 'Preparando sua conta...' : 'Concluir'}
                                <MdCheckCircle className="text-lg" />
                            </button>
                        ) : (
                            <button
                                type="button"
                                className="btn btn-secondary gap-1"
                                onClick={goNext}
                                disabled={submitting}
                            >
                                Continuar
                                <MdArrowForward className="text-lg" />
                            </button>
                        )}
                    </div>
                </footer>
            </div>
        </div>
    )
}
